import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Save, Loader2, Calendar, Globe, MapPin, Mail, Building, 
  FileText, Type, RefreshCw, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGlobalContent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    event: true,
    branding: true,
    contact: true,
    social: false,
    footer: false
  });
  
  const [content, setContent] = useState({
    // Event Details
    event_name: 'World Nuclear Congress',
    event_year: '2027',
    event_dates_display: 'Coming January 2027',
    event_location: 'University of Tennessee, Knoxville',
    event_venue: 'Howard H. Baker Jr. Center for Public Policy',
    event_address: '1640 Cumberland Avenue, Knoxville, TN 37996',
    
    // Featured Nation
    featured_nation: 'Romania',
    featured_nation_year: '2026',
    featured_nation_description: 'Romania will serve as the Featured Allied Nation, reflecting its leadership in nuclear deployment and its strong relationship with the United States.',
    
    // Branding
    site_title: 'World Nuclear Congress',
    tagline: 'Leading the Conversation on Nuclear Energy',
    meta_description: 'The premier gathering of nuclear industry leaders, policymakers, and experts.',
    
    // Contact Info
    primary_contact_name: 'Michelle',
    primary_contact_email: 'michelle@ceeleaders.com',
    secondary_contact_name: 'Nastasia',
    secondary_contact_email: 'nastasia@ceeleaders.com',
    general_inquiry_email: 'contact@ceeleaders.com',
    
    // Registration
    registration_url: 'https://utconferences.eventsair.com/cee/registration/Site/Register',
    registration_open: true,
    registration_cta_text: 'Register Now',
    
    // Footer
    footer_copyright: '© 2027 World Nuclear Congress. All rights reserved.',
    footer_tagline: 'Advancing nuclear energy for a sustainable future.'
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchContent();
  }, [navigate]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/content/global`);
      if (response.ok) {
        const data = await response.json();
        setContent(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch global content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/content/global`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });
      
      if (response.ok) {
        toast.success('Global content saved successfully');
      } else {
        toast.error('Failed to save content');
      }
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const Section = ({ id, title, icon: Icon, description, children }) => {
    const isExpanded = expandedSections[id];
    return (
      <Card className="overflow-hidden">
        <button
          onClick={() => toggleSection(id)}
          className="w-full"
        >
          <CardHeader className="pb-2 hover:bg-gray-50 transition-colors">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#00457f]/10 rounded-lg">
                  <Icon className="w-5 h-5 text-[#00457f]" />
                </div>
                <div className="text-left">
                  <CardTitle className="text-base">{title}</CardTitle>
                  {description && (
                    <CardDescription className="text-sm">{description}</CardDescription>
                  )}
                </div>
              </div>
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </CardHeader>
        </button>
        {isExpanded && (
          <CardContent className="pt-4 border-t">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#00457f]" />
              Global Content
            </h1>
            <p className="text-gray-600 mt-1">
              Edit site-wide content that appears across all pages
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#00457f] hover:bg-[#003366]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        <div className="space-y-4">
          {/* Event Details */}
          <Section
            id="event"
            title="Event Details"
            icon={Calendar}
            description="Core event information displayed across the site"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Event Name</Label>
                <Input
                  value={content.event_name}
                  onChange={(e) => updateField('event_name', e.target.value)}
                  placeholder="World Nuclear Congress"
                />
              </div>
              <div>
                <Label>Event Year</Label>
                <Input
                  value={content.event_year}
                  onChange={(e) => updateField('event_year', e.target.value)}
                  placeholder="2027"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Event Dates Display</Label>
                <Input
                  value={content.event_dates_display}
                  onChange={(e) => updateField('event_dates_display', e.target.value)}
                  placeholder="e.g., Coming February 2027 or February 12-13, 2027"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is the text shown in navigation and headers
                </p>
              </div>
              <div>
                <Label>Event Location</Label>
                <Input
                  value={content.event_location}
                  onChange={(e) => updateField('event_location', e.target.value)}
                  placeholder="University of Tennessee, Knoxville"
                />
              </div>
              <div>
                <Label>Venue Name</Label>
                <Input
                  value={content.event_venue}
                  onChange={(e) => updateField('event_venue', e.target.value)}
                  placeholder="Howard H. Baker Jr. Center"
                />
              </div>
              <div className="md:col-span-2">
                <Label>Venue Address</Label>
                <Input
                  value={content.event_address}
                  onChange={(e) => updateField('event_address', e.target.value)}
                  placeholder="1640 Cumberland Avenue, Knoxville, TN 37996"
                />
              </div>
            </div>

            {/* Featured Nation */}
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-medium text-gray-900 mb-3">Featured Allied Nation</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Country Name</Label>
                  <Input
                    value={content.featured_nation}
                    onChange={(e) => updateField('featured_nation', e.target.value)}
                    placeholder="Romania"
                  />
                </div>
                <div>
                  <Label>Featured Year</Label>
                  <Input
                    value={content.featured_nation_year}
                    onChange={(e) => updateField('featured_nation_year', e.target.value)}
                    placeholder="2026"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Textarea
                    value={content.featured_nation_description}
                    onChange={(e) => updateField('featured_nation_description', e.target.value)}
                    rows={3}
                    placeholder="Brief description of the featured nation's role..."
                  />
                </div>
              </div>
            </div>
          </Section>

          {/* Branding */}
          <Section
            id="branding"
            title="Branding & SEO"
            icon={Type}
            description="Site title, tagline, and meta information"
          >
            <div className="space-y-4">
              <div>
                <Label>Site Title</Label>
                <Input
                  value={content.site_title}
                  onChange={(e) => updateField('site_title', e.target.value)}
                  placeholder="World Nuclear Congress"
                />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input
                  value={content.tagline}
                  onChange={(e) => updateField('tagline', e.target.value)}
                  placeholder="Leading the Conversation on Nuclear Energy"
                />
              </div>
              <div>
                <Label>Meta Description</Label>
                <Textarea
                  value={content.meta_description}
                  onChange={(e) => updateField('meta_description', e.target.value)}
                  rows={2}
                  placeholder="Brief description for search engines..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for SEO - appears in search results
                </p>
              </div>
            </div>
          </Section>

          {/* Contact Information */}
          <Section
            id="contact"
            title="Contact Information"
            icon={Mail}
            description="Contact details shown on the website"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Primary Contact Name</Label>
                <Input
                  value={content.primary_contact_name}
                  onChange={(e) => updateField('primary_contact_name', e.target.value)}
                />
              </div>
              <div>
                <Label>Primary Contact Email</Label>
                <Input
                  type="email"
                  value={content.primary_contact_email}
                  onChange={(e) => updateField('primary_contact_email', e.target.value)}
                />
              </div>
              <div>
                <Label>Secondary Contact Name</Label>
                <Input
                  value={content.secondary_contact_name}
                  onChange={(e) => updateField('secondary_contact_name', e.target.value)}
                />
              </div>
              <div>
                <Label>Secondary Contact Email</Label>
                <Input
                  type="email"
                  value={content.secondary_contact_email}
                  onChange={(e) => updateField('secondary_contact_email', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>General Inquiry Email</Label>
                <Input
                  type="email"
                  value={content.general_inquiry_email}
                  onChange={(e) => updateField('general_inquiry_email', e.target.value)}
                />
              </div>
            </div>
          </Section>

          {/* Registration */}
          <Section
            id="registration"
            title="Registration"
            icon={FileText}
            description="Registration link and button settings"
          >
            <div className="space-y-4">
              <div>
                <Label>Registration URL</Label>
                <Input
                  value={content.registration_url}
                  onChange={(e) => updateField('registration_url', e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>CTA Button Text</Label>
                <Input
                  value={content.registration_cta_text}
                  onChange={(e) => updateField('registration_cta_text', e.target.value)}
                  placeholder="Register Now"
                />
              </div>
            </div>
          </Section>

          {/* Footer */}
          <Section
            id="footer"
            title="Footer Content"
            icon={Building}
            description="Copyright and footer text"
          >
            <div className="space-y-4">
              <div>
                <Label>Copyright Text</Label>
                <Input
                  value={content.footer_copyright}
                  onChange={(e) => updateField('footer_copyright', e.target.value)}
                  placeholder="© 2027 World Nuclear Congress. All rights reserved."
                />
              </div>
              <div>
                <Label>Footer Tagline</Label>
                <Input
                  value={content.footer_tagline}
                  onChange={(e) => updateField('footer_tagline', e.target.value)}
                  placeholder="Advancing nuclear energy for a sustainable future."
                />
              </div>
            </div>
          </Section>
        </div>

        {/* Preview Note */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Changes to global content will be reflected across all pages 
              of the website after saving. Some content may require a page refresh to appear.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminGlobalContent;
